﻿import {
	CurrentUser,
	IdpAuth,
	IdpClientConfig,
	IdpConfig,
	IdpStorageKeys,
	IdpTokenType,
	ObjectType,
	LinkAuthError,
	isCurrentUser,
	LinkAuthException
} from 'phoenix/domain';
import { 
	IDictionary,
	IStorageAccessor
} from 'util/domain';
import { EnvironmentService } from 'phoenix/services/environmentService';
import SettingsService from 'phoenix/services/settingsService';
import { IUriPathService } from 'phoenix/services/uriPathService';
import { LinkAuthExceptionFactory } from 'phoenix/services';
import { 
	LinkAuthorizationReponse, 
	LinkAuthorizationResponseLegacy, 
	LinkAuthorizationErrorType
} from 'phoenix/domain/link-authorization-response';

interface AuthToken {
	type: IdpTokenType;
	value: string;
}

interface UserStat {
	Id: string;
	Label: string;
	Count: number;
}

const LOGIN_REDIRECT_KEY = 'login-redirect';

export abstract class AuthService {
	public abstract readonly idpSettings:IdpClientConfig;

	protected authData:IdpAuth;
	protected authToken:AuthToken;
	protected idpConfig:IdpConfig;
	protected idToken:string;
	protected latestLogins:string[] = [];
	protected pxUriPathService:IUriPathService;
	protected userData:CurrentUser;
	protected userStats:IDictionary<UserStat>;
	protected verifiedToken = false;

	protected _privileges;
	public get privileges() {
		if(!this._privileges && this.authToken) {
			this._privileges = this.createPrivs(this.userData.Embedded.Privileges);
		}

		return this._privileges || {};
	}

	public get lastLinkPassword() {
		return this.sessionStorageService.getStoredValue(IdpStorageKeys.LAST_LINK_PASSWORD);
	}
	public set lastLinkPassword(value:string) {
		if(value === null || !angular.isDefined(value)){
			this.sessionStorageService.removeStoredValue(IdpStorageKeys.LAST_LINK_PASSWORD);
		}
		else {
			this.sessionStorageService.setStoredValue(IdpStorageKeys.LAST_LINK_PASSWORD, value);
		}
	}

	/**
	* @ngdoc property
	* @name pxAuthService#deploymentId
	* @propertyOf phoenix.pxAuthService
	* @kind property
	*
	* @description
	* Returns the deployment ID of the authenticated user, or null
	* if the user is a system administrator.
	*
	* @returns {int} The user's deployment ID.
	*/
	public get deploymentId() { return this.userData ? this.userData.DeploymentId : null; }

	/**
	* @ngdoc property
	* @name pxAuthService#deploymentGroupId
	* @propertyOf phoenix.pxAuthService
	* @kind property
	*
	* @description
	* Returns the deployment group ID of the authenticated user, or null
	* if the user is a system administrator.
	*
	* @returns {int} The user's deployment group ID.
	*/
	public get deploymentGroupId() { return this.userData && this.userData.DeploymentGroupId > -1 ? this.userData.DeploymentGroupId : null; }

	/**
	 * @ngdoc property
	 * @name pxAuthService#displayName
	 * @propertyOf phoenix.pxAuthService
	 * @kind property
	 *
	 * @description
	 * Returns the display name of the authenticated user, or null
	 * if the user is not authenticated.
	 *
	 * @returns {string} The user's display name.
	 */
	public get displayName() { return  this.user? this.user.UserName : null}

	public get isAdmin() { return this.isSysAdmin || this.isTenantAdmin || this.isSiteAdmin || this.isUserAdmin; }
	public get isAuthenticated() { return !!this.token;	}
	public get isShareByLinkUser() { return this.user? this.user.UserName === 'ShareByLinkUser' : false; }
	public get isSiteAdmin() { return this.user ? this.user.Embedded.Roles.indexOf('SiteAdmin') >= 0 : false; }
	public get isSysAdmin() { return this.userData ? this.userData.IsSysAdmin : false; }
	public get isTenantAdmin() { return this.user ? this.user.Embedded.Roles.indexOf('TenantAdmin') >= 0 : false; }
	public get isUserAdmin() { return this.user ? this.user.Embedded.Roles.indexOf('UserAdmin') >= 0 : false; }

	/**
	* @ngdoc property
	* @name pxAuthService#systemName
	* @propertyOf phoenix.pxAuthService
	* @kind property
	*
	* @description
	* Returns the system name of the authenticated user, or null
	* if the user is a system administrator.
	*
	* @returns {string} The user's system name.
	*/
	public get systemName() { return this.userData ? this.userData.SystemName : null; }

	/**
	 * @ngdoc property
	 * @name pxAuthService#token
	 * @propertyOf phoenix.pxAuthService
	 * @kind property
	 *
	 * @description
	 * Returns the authenticated users Phoenix token.
	 *
	 * @returns {string} The user's Phoenix token
	 */
	public get token() {
		return this.authToken || null; 
	}

	/**
	 * @ngdoc property
	 * @name pxAuthService#user
	 * @propertyOf phoenix.pxAuthService
	 * @kind property
	 *
	 * @description
	 * Gets the user object from the authentication token.
	 *
	 * @returns {Object} An object containing user information.
	 */
	public get user() {
		return this.userData ? this.userData.Embedded.User : null;
	}

	/**
	* @ngdoc property
	* @name pxAuthService#userId
	* @propertyOf phoenix.pxAuthService
	* @kind property
	*
	* @description
	* The user ID of the authenticated user.
	*
	* @returns {int} The user's user ID.
	*/
	public get userId() { return this.user ? this.user.UserId : null; }

	/**
	 * @ngdoc property
	 * @name pxAuthService#userPreviousLogin
	 * @propertyOf phoenix.pxAuthService
	 * @kind property
	 *
	 * @description
	 * Retrieves the user's last login date (previous to the current session)
	 *
	 * Calling getUserPreviousLogin will force updates to this
	 * property.
	 *
	 * @returns {Date} The user's last login date (previous to the current session) or null if the user
	 * is not authenticated or has never logged in
	 */
	public get userPreviousLogin() {
		if(this.latestLogins.length > 1) {
			return new Date(this.latestLogins[1]);
		}
		else {
			return null;
		}
	}

	/**
	 * @ngdoc property
	 * @name pxAuthService#userStatistics
	 * @propertyOf phoenix.pxAuthService
	 * @kind property
	 *
	 * @description
	 * A set of usage statistics for the authenticated user.
	 *
	 * Calling getUserStatistics will force updates to this
	 * property.
	 *
	 * @returns {array} The user's usage data or null if the user
	 * is not authenticated
	 */
	public get userStatistics() { return this.userStats; }

	/**
	* @ngdoc property
	* @name pxAuthService#redirectPath
	* @propertyOf phoenix.pxAuthService
	* @kind property
	*
	* @description
	* Get and set the redirect path that will be used to redirect users after a successful login.
	*
	* @returns {string} A url path that points to a location in the app.
	*/
	public get redirectPath(): string {
		let redirect = this.sessionStorageService.getStoredValue(LOGIN_REDIRECT_KEY);
		if (!redirect) {
			redirect = '/';
		}

		return redirect;
	}
	public set redirectPath(val: string) {
		this.sessionStorageService.setStoredValue(LOGIN_REDIRECT_KEY, val);
	}

	public get rememberedEmail() {
		return this.localStorageService.getStoredValue(IdpStorageKeys.REMEMBERED_EMAIL);
	}
	public set rememberedEmail(email) {
		if(!angular.isDefined(email) || email === null){
			this.localStorageService.removeStoredValue(IdpStorageKeys.REMEMBERED_EMAIL);
		}
		else {
			this.localStorageService.setStoredValue(IdpStorageKeys.REMEMBERED_EMAIL, email.toString());
		}
	}

	public constructor(
		protected readonly $window:ng.IWindowService,
		protected readonly $http:ng.IHttpService,
		protected readonly $q:ng.IQService,
		protected readonly $injector:ng.auto.IInjectorService,
		protected readonly $location:ng.ILocationService,
		protected readonly settingsService:SettingsService,
		protected readonly localStorageService:IStorageAccessor,
		protected readonly sessionStorageService:IStorageAccessor,
		protected readonly pxEnvironmentService:EnvironmentService,
		protected readonly pxLinkAuthExceptionFactory:LinkAuthExceptionFactory
	) {
	}

	public abstract handle401();

	public abstract invalidateLogin(inMemoryOnly?:boolean): ng.IPromise<string>;

	public abstract sendToIdp(emailAddress:string, rememberMe?:boolean): ng.IPromise<void>;

	/**
	 * Stop gap for change password, since there have been some user complaints that they cannot
	 * change it in the app. This is the old functionality and will need to be removed once we
	 * actually have users using a federated authentication provider
	 */
	public changePassword(password: string, newPassword: string, confirmPassword: string) {
		let data = {
			OldPassword: password,
			NewPassword: newPassword,
			VerifyPassword: confirmPassword
		};

		let url = `${this.settingsService.serverAPIPath}/users/${this.user.UserId}/password`;
		return this.$http.post(url, data);
	}

	/**
	* @ngdoc method
	* @name pxAuthService#establishLinkToken
	* @methodOf phoenix.pxAuthService
	* @kind function
	*
	* @description
	* Requests a Phoenix token based on a share-by-link hash. 
	* @param {string} token Share-by-link hasj to validate with the server
	*
	* @returns {Deferred} A promise that resolves if the hash is valid;
	* otherwise an exception is thrown
	*/
	public establishLinkUser(objectType:ObjectType, token:string, password:string, recipient:string):ng.IPromise<void> {
		this.guardPathService();
		let p:ng.IPromise<ng.IHttpResponse<any>>;
		let serviceParams: any = { 
			ref: token, 
			system: +token.split('-')[0]
		};
		let shareUri = (objectType === ObjectType.FOLDER) ? 
			'/f/shares/{ref}' : 
			'/documents/shares/{ref}';
		shareUri += (recipient ? '/{recipient}' : '') + '/auth/?system={system}';

		if(recipient){
			serviceParams.recipient = recipient;
		}

		shareUri = this.pxUriPathService.resolvePath(shareUri, serviceParams);
		if(recipient) {
			p = this.$http.post(shareUri, {
				Pin: password
			});
		}
		else if (password) {
			p = this.$http.post(shareUri, {
				Password: password
			});
		}
		else {
			p = this.$http.get(shareUri);
		}

		return p.then((resp:ng.IHttpResponse<LinkAuthorizationResponseLegacy|CurrentUser>) => {
			let p:ng.IPromise<void> = this.$q.resolve();
			const data = resp.data;
			
			if (this.isLinkAuthResponseLegacy(data)){
				if(data.InvalidPassword){
					p = this.$q.reject(resp);
				}
			}
			else if (isCurrentUser(data)){
				if(data.IsExpired){
					p = this.$q.reject(resp);
				} 
				else {
					p = this.invalidateLogin(true)
						.then(() => {
							this.storeToken(data.Token, 'PHOENIX-TOKEN');
							this.userData = data;
						});
				}
			}	
			
			return p;
		})
		.catch((e:ng.IHttpResponse<LinkAuthorizationReponse|LinkAuthorizationResponseLegacy|CurrentUser|string>) => {
			return this.$q.reject(this.generateLinkAuthException(e));
		});
	}	

	/**
	 * @ngdoc method
	 * @name pxAuthService#getUserStatistics
	 * @methodOf phoenix.pxAuthService
	 * @kind function
	 *
	 * @description
	 * Gets usage statistics for the authenticated user
	 *
	 * @returns {Deferred} A promise that will resolve with the
	 * user statistics if authenticated, otherwise null.
	 *
	 */
	public getUserStatistics(): ng.IPromise<IDictionary<UserStat> |void>  {
		if (!this.userData) {
			return this.$q.resolve();
		}

		let uri = this.userData.Embedded.User.Links.StatisticsUri;

		return this.$http.get<UserStat[]>(uri)
			.then(response => {
				if (angular.isArray(response.data)) {
					this.userStats = response.data.reduce((stats, item) => {
						stats[item.Id] = item;
						delete item.Id;
						return stats;
					}, {} as IDictionary<UserStat>);
				}

				return this.userStats;
			});
	}

	public initializeWithBearerToken(token:string) {
		this.storeToken(token, 'Bearer');

		return this.hydrateUserData();
	}

	/**
	* @ngdoc method
	* @name pxAuthService#establishLinkToken
	* @methodOf phoenix.pxAuthService
	* @kind function
	*
	* @description
	* Requests a secure PIN be generated and send to the appropriate user via email
	* itemType:ObjectType, token:string, recipient:string
	* @param {number} itemType ShareBase item enum.
	* @param {string} token Token from the share link.
	* @param {string} recipient Recipient ID from the share lin.
	*
	* @returns {Deferred} A promise that resolve when the server notifies the user by email.
	*/
	public requestAccessCode(itemType:ObjectType, token:string, recipient:string) {
		this.guardPathService();
		let systemId = +token.split('-')[0];
		let pinRequestUri = itemType === ObjectType.FOLDER ? 
			'/f/shares/{ref}/{recipient}/requestpin?system={system}' : 
			'/documents/shares/{ref}/{recipient}/requestpin?system={system}';
		pinRequestUri = this.pxUriPathService.resolvePath(pinRequestUri, {
			ref: token,
			recipient: recipient,
			system: systemId
		});

		return this.$http.get(pinRequestUri);
	}

	public resetPassword(authToken, newPassword): ng.IPromise<any> {
		return this.$http.post(this.settingsService.serverAPIPath + '/passwordcallback?auth=' + authToken, {
				NewPassword: newPassword
			})
			.then(function (response) {
				return response.data;
			});
	}

	// Does nothing since we won't need an implimentation for the Hybrid Client due to
	// Minotaur using a third-party auth module
	public validate(authData: IdpAuth) {
		return this.$q.resolve();
	}

	protected getIdpConfig() {
		let p = this.$q.resolve(this.idpConfig);
		if(!this.idpConfig){
			let uri = `${this.idpSettings.path}/.well-known/openid-configuration`;
			p = this.$http.get(uri).then(response => this.idpConfig = (response.data as IdpConfig));
		}
		return p;
	}

	protected hydrateUserData() {
		return this.getUserData()
			.then(userData => {
				this.userData = userData;

				let latestLoginsUri = `${this.settingsService.serverAPIPath}/users/${this.user.UserId}/last-logon-dates?count=2`;
				
				return this.$http.get<string[]>(latestLoginsUri)
					.then(resp => {
						this.latestLogins = resp.data;
					});
			});
	}

	protected storeToken(token:string, type:IdpTokenType) {
		this.authToken = {
			type: type,
			value: token
		};
	}

	private createPrivs(privList) {
		return privList.reduce(function(privs, priv){
			// map privlege namespace path onto properties of object
			priv.split('/')
				.reduce(function(section, sectionName, i, sectionsArr){
					// camel case the properties
					sectionName = sectionName.charAt(0).toLowerCase() + sectionName.slice(1);

					//If the section (namespace) doesn't exist, create an object for it
					//It also always checks at the leaf node level, like system.admin.createUser, to be truthy
					if(!(sectionName in section)){
						section[sectionName] = Object.create(null);
					}

					return section[sectionName];
				}, privs);

			return privs;
		}, {});
	}

	private getUserData() {
		let loginUserUri = `${this.settingsService.serverAPIPath}/get-login-user`;

		let promises = [
			this.getIdpConfig()
				.then(idpConfig => {
					return this.$http.get(idpConfig.userinfo_endpoint)
						.then(resp => resp.data);
			}),
			this.$http.get(loginUserUri)
				.then(resp => resp.data)
		];
		return this.$q.all(promises)
			.then(infos => {
				return infos.reduce((userData, info) => {
					return angular.extend(
						userData,
						info
					);
				}, {})
		});
	}

	private guardPathService() {
		if(!this.pxUriPathService){
			this.pxUriPathService = this.$injector.get('pxUriPathService');
		}
	}

	private generateLinkAuthException(e:ng.IHttpResponse<LinkAuthorizationReponse|LinkAuthorizationResponseLegacy|CurrentUser|string>):LinkAuthException{
		const data = e.data;
		let errorCode = LinkAuthError.DEFAULT;
		if (e.status === 404){
			errorCode = LinkAuthError.LINK_NOT_FOUND;
		}
		else if (this.isLinkAuthResponse(data)) {
			if(data.type === LinkAuthorizationErrorType.InvalidPassword) {
				errorCode = LinkAuthError.INVALID_PASSWORD;
			} 
			else if (data.type === LinkAuthorizationErrorType.InvalidAccessCode){
				errorCode = LinkAuthError.INVALID_ACCESS_CODE;
			}
		} 
		else if (this.isLinkAuthResponseLegacy(data)) {
			if (data.InvalidPassword){
				errorCode = LinkAuthError.INVALID_PASSWORD;
			}
			else if (data.InvalidAccessCode || data.InvalidPin){
				errorCode = LinkAuthError.INVALID_ACCESS_CODE;
			}
		}	
		else if (isCurrentUser(data)) {
			if (data.IsExpired){
				errorCode = LinkAuthError.LINK_EXPIRED;
			}
		}	
		return this.pxLinkAuthExceptionFactory.create(errorCode);
	}

	private isLinkAuthResponse(obj:any):obj is LinkAuthorizationReponse {
		return obj && angular.isDefined(obj.type) 
				   && angular.isDefined(obj.status) 
				   && angular.isDefined(obj.title) 
				   && angular.isDefined(obj.instance) 
				   && angular.isDefined(obj.errors);
	}

	private isLinkAuthResponseLegacy(obj:any):obj is LinkAuthorizationResponseLegacy {
		return obj && (angular.isDefined(obj.InvalidAccessCode) 
				   || angular.isDefined(obj.InvalidPassword)
				   || angular.isDefined(obj.InvalidPin));
	}

}


// WEBPACK FOOTER //
// ./src/phoenix/services/auth/authService.ts